// PWA Utility Functions
export class PWAManager {
  private static instance: PWAManager;
  private deferredPrompt: any = null;
  private isInstalled = false;
  private registration: ServiceWorkerRegistration | null = null;

  static getInstance(): PWAManager {
    if (!PWAManager.instance) {
      PWAManager.instance = new PWAManager();
    }
    return PWAManager.instance;
  }

  constructor() {
    this.init();
  }

  private async init() {
    // Check if already installed
    this.checkInstallStatus();
    
    // Listen for install prompt
    this.setupInstallPrompt();
    
    // Register service worker
    await this.registerServiceWorker();
    
    // Setup push notifications
    this.setupPushNotifications();
  }

  private checkInstallStatus() {
    // Check various PWA installation indicators
    this.isInstalled = 
      window.navigator.standalone === true || // iOS
      window.matchMedia('(display-mode: standalone)').matches || // Android
      window.matchMedia('(display-mode: minimal-ui)').matches;
  }

  private setupInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {
      console.log('[PWA] Install prompt available');
      e.preventDefault();
      this.deferredPrompt = e;
      
      // Dispatch custom event for components to listen
      window.dispatchEvent(new CustomEvent('pwa-install-available'));
    });

    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed');
      this.isInstalled = true;
      this.deferredPrompt = null;
      
      // Dispatch custom event
      window.dispatchEvent(new CustomEvent('pwa-installed'));
    });
  }

  async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (!('serviceWorker' in navigator)) {
      console.warn('[PWA] Service Workers not supported');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      this.registration = registration;

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // New version available
                window.dispatchEvent(new CustomEvent('pwa-update-available'));
              } else {
                // First install
                console.log('[PWA] Service Worker installed for first time');
              }
            }
          });
        }
      });

      console.log('[PWA] Service Worker registered successfully');
      return registration;
    } catch (error) {
      console.error('[PWA] Service Worker registration failed:', error);
      return null;
    }
  }

  async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      console.warn('[PWA] No install prompt available');
      return false;
    }

    try {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      
      console.log('[PWA] Install prompt result:', outcome);
      
      if (outcome === 'accepted') {
        this.deferredPrompt = null;
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[PWA] Install prompt failed:', error);
      return false;
    }
  }

  canInstall(): boolean {
    return !this.isInstalled && this.deferredPrompt !== null;
  }

  isAppInstalled(): boolean {
    return this.isInstalled;
  }

  async setupPushNotifications(): Promise<PushSubscription | null> {
    if (!this.registration) {
      console.warn('[PWA] No service worker registration for push notifications');
      return null;
    }

    if (!('PushManager' in window)) {
      console.warn('[PWA] Push messaging not supported');
      return null;
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission !== 'granted') {
        console.warn('[PWA] Push notification permission denied');
        return null;
      }

      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create new subscription
        const vapidPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
        
        if (!vapidPublicKey) {
          console.warn('[PWA] VAPID public key not configured');
          return null;
        }

        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
        });

        // Send subscription to server
        await this.sendSubscriptionToServer(subscription);
      }

      console.log('[PWA] Push notifications enabled');
      return subscription;
    } catch (error) {
      console.error('[PWA] Push notification setup failed:', error);
      return null;
    }
  }

  private async sendSubscriptionToServer(subscription: PushSubscription) {
    try {
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscription),
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription');
      }

      console.log('[PWA] Subscription saved to server');
    } catch (error) {
      console.error('[PWA] Failed to save subscription:', error);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    
    return outputArray;
  }

  async updateServiceWorker() {
    if (!this.registration) {
      return;
    }

    try {
      await this.registration.update();
      
      // Skip waiting and activate new service worker
      if (this.registration.waiting) {
        this.registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      
      // Reload page to use new service worker
      window.location.reload();
    } catch (error) {
      console.error('[PWA] Service worker update failed:', error);
    }
  }

  async requestPersistentStorage(): Promise<boolean> {
    if (!('storage' in navigator) || !('persist' in navigator.storage)) {
      console.warn('[PWA] Persistent storage not supported');
      return false;
    }

    try {
      const persistent = await navigator.storage.persist();
      console.log(`[PWA] Persistent storage: ${persistent}`);
      return persistent;
    } catch (error) {
      console.error('[PWA] Persistent storage request failed:', error);
      return false;
    }
  }

  async getStorageEstimate() {
    if (!('storage' in navigator) || !('estimate' in navigator.storage)) {
      return null;
    }

    try {
      const estimate = await navigator.storage.estimate();
      return {
        quota: estimate.quota,
        usage: estimate.usage,
        available: (estimate.quota || 0) - (estimate.usage || 0),
        percentage: estimate.quota ? Math.round((estimate.usage || 0) / estimate.quota * 100) : 0
      };
    } catch (error) {
      console.error('[PWA] Storage estimate failed:', error);
      return null;
    }
  }

  // Background sync for offline data
  async scheduleBackgroundSync(tag: string = 'nutrition-sync') {
    if (!this.registration || !('sync' in this.registration)) {
      console.warn('[PWA] Background sync not supported');
      return false;
    }

    try {
      await this.registration.sync.register(tag);
      console.log(`[PWA] Background sync scheduled: ${tag}`);
      return true;
    } catch (error) {
      console.error('[PWA] Background sync registration failed:', error);
      return false;
    }
  }

  // Network status monitoring
  setupNetworkMonitoring() {
    const updateOnlineStatus = () => {
      const event = new CustomEvent('network-status-changed', {
        detail: { online: navigator.onLine }
      });
      window.dispatchEvent(event);
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Initial status
    updateOnlineStatus();
  }
}

// Utility functions
export const pwa = PWAManager.getInstance();

export const getInstallPromptData = () => ({
  canInstall: pwa.canInstall(),
  isInstalled: pwa.isAppInstalled()
});

export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  
  return await Notification.requestPermission();
};

export const showLocalNotification = (title: string, options?: NotificationOptions) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    return new Notification(title, {
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      ...options
    });
  }
  return null;
};

export const isStandalone = (): boolean => {
  return window.navigator.standalone === true || 
         window.matchMedia('(display-mode: standalone)').matches ||
         window.matchMedia('(display-mode: minimal-ui)').matches;
};

export const canShare = (): boolean => {
  return 'share' in navigator;
};

export const shareContent = async (data: ShareData): Promise<boolean> => {
  if (!canShare()) {
    return false;
  }

  try {
    await navigator.share(data);
    return true;
  } catch (error) {
    console.error('[PWA] Share failed:', error);
    return false;
  }
};

// Storage utilities for offline data
export const OfflineStorage = {
  async set(key: string, data: any): Promise<boolean> {
    try {
      const serialized = JSON.stringify({
        data,
        timestamp: Date.now(),
        version: 1
      });
      
      localStorage.setItem(`nutriquests_${key}`, serialized);
      return true;
    } catch (error) {
      console.error('[PWA] Offline storage set failed:', error);
      return false;
    }
  },

  async get<T>(key: string): Promise<T | null> {
    try {
      const serialized = localStorage.getItem(`nutriquests_${key}`);
      if (!serialized) return null;

      const parsed = JSON.parse(serialized);
      return parsed.data as T;
    } catch (error) {
      console.error('[PWA] Offline storage get failed:', error);
      return null;
    }
  },

  async remove(key: string): Promise<boolean> {
    try {
      localStorage.removeItem(`nutriquests_${key}`);
      return true;
    } catch (error) {
      console.error('[PWA] Offline storage remove failed:', error);
      return false;
    }
  },

  async clear(): Promise<boolean> {
    try {
      const keys = Object.keys(localStorage).filter(key => 
        key.startsWith('nutriquests_')
      );
      keys.forEach(key => localStorage.removeItem(key));
      return true;
    } catch (error) {
      console.error('[PWA] Offline storage clear failed:', error);
      return false;
    }
  }
};